import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type {
  ColDef,
  GetRowIdParams,
  RowClickedEvent,
  CellMouseDownEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useScans } from '../hooks/useScans';
import type { ScanListItem } from '../types/scans';
import { ErrorDisplay } from './ErrorDisplay';
import { Layout } from './Layout';
import {
  TimeAgoCellRenderer,
  NumberCellRenderer,
} from './ag-grid/cellRenderers';
import './ag-grid/styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;

function ErrorsCellRenderer({ value }: { value: string[] | null }) {
  if (!value || value.length === 0) return <span>-</span>;
  const errorCount = value.length;
  const preview =
    value[0].length > 50 ? value[0].slice(0, 50) + '...' : value[0];
  const tooltip = value.join('\n');
  return (
    <span className="text-red-600 dark:text-red-400" title={tooltip}>
      {errorCount > 1 ? `${errorCount} errors: ${preview}` : preview}
    </span>
  );
}

export function ScanList() {
  const gridRef = useRef<AgGridReact<ScanListItem>>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const { scans, isLoading, error, total, page, setPage, setSearch } = useScans(
    {
      page: 1,
      limit: PAGE_SIZE,
      search: searchQuery,
    }
  );

  if (!isLoading && !hasLoaded) {
    setHasLoaded(true);
  }

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<ScanListItem>) => {
      const mouseEvent = event.event as MouseEvent | undefined;
      if (
        mouseEvent?.button === 1 ||
        mouseEvent?.ctrlKey ||
        mouseEvent?.metaKey
      ) {
        return;
      }
      const scan = event.data;
      if (!scan) return;
      window.location.href = `/scan/${encodeURIComponent(scan.scan_folder)}`;
    },
    []
  );

  const handleCellMouseDown = useCallback(
    (event: CellMouseDownEvent<ScanListItem>) => {
      const mouseEvent = event.event as MouseEvent;
      if (mouseEvent.button === 1 || mouseEvent.ctrlKey || mouseEvent.metaKey) {
        const scan = event.data;
        if (!scan) return;
        window.open(`/scan/${encodeURIComponent(scan.scan_folder)}`, '_blank');
      }
    },
    []
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const columnDefs = useMemo<ColDef<ScanListItem>[]>(
    () => [
      {
        field: 'meta_name',
        headerName: 'Name',
        width: 350,
        valueFormatter: params => params.value || '-',
      },
      {
        field: 'job_id',
        headerName: 'Job ID',
        width: 220,
        valueFormatter: params => params.value || '-',
      },
      {
        field: 'scanner_result_count',
        headerName: 'Results',
        width: 100,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'errors',
        headerName: 'Errors',
        width: 200,
        cellRenderer: ErrorsCellRenderer,
      },
      {
        field: 'timestamp',
        headerName: 'Timestamp',
        width: 150,
        cellRenderer: TimeAgoCellRenderer,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<ScanListItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<ScanListItem>) => params.data.scan_id,
    []
  );

  if (error) {
    return (
      <Layout>
        <ErrorDisplay message={error.toString()} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        <div className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-4 py-2 shrink-0">
          <form
            onSubmit={e => e.preventDefault()}
            className="flex items-center gap-3"
          >
            <div className="flex-1 relative max-w-md">
              <input
                ref={searchInputRef}
                type="search"
                placeholder="Search scans..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full h-8 px-3 text-sm rounded border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-300 dark:focus:border-brand-300"
              />
              {isLoading && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 dark:border-gray-700 border-t-brand-600 dark:border-t-brand-300 rounded-full"></div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* AG Grid */}
        <div className="flex-1 overflow-hidden relative">
          {!hasLoaded && (
            <div className="absolute inset-0 bg-white dark:bg-gray-900 z-10 p-4">
              <div className="space-y-2">
                {Array.from({ length: 15 }).map((_, i) => (
                  <div key={i} className="flex gap-4 animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-48"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-36"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-36"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-40"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {scans.length === 0 && hasLoaded ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {searchQuery
                ? `No scans found matching "${searchQuery}"`
                : 'No scans found'}
            </div>
          ) : (
            <div className="ag-theme-quartz h-full w-full">
              <AgGridReact<ScanListItem>
                ref={gridRef}
                rowData={scans}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                onRowClicked={handleRowClicked}
                onCellMouseDown={handleCellMouseDown}
                animateRows={false}
                suppressCellFocus={true}
                domLayout="normal"
              />
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 px-4 py-2 flex items-center justify-between shrink-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}{' '}
              of {total}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || isLoading}
                className={`h-7 px-3 text-xs font-medium rounded ${
                  page === 1 || isLoading
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                ← Prev
              </button>
              <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages || isLoading}
                className={`h-7 px-3 text-xs font-medium rounded ${
                  page === totalPages || isLoading
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
